import React from "react";
import { parseErrorMessage } from "@/lib/parseErrorMessage";
// ============================================================================
// useTemplates Hook
// ============================================================================
// Purpose: React hook for all template-related Supabase operations
// ============================================================================

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdTemplate, AdTemplateFormData, AdTemplateListItem } from "@/types/template";
import { useToast } from "@/hooks/use-toast";

export function useTemplates() {
  const [templates, setTemplates] = useState<AdTemplateListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  /**
   * Fetch all templates for current user
   * Optionally filter by ad_account_id
   */
  const fetchTemplates = async (adAccountId?: string) => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from("ad_templates")
        .select("id, name, description, ad_account_id, status, created_at, updated_at")
        .order("updated_at", { ascending: false });

      // Filter by ad account strictly
      if (adAccountId) {
        query = query.eq('ad_account_id', adAccountId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setTemplates(data || []);
    } catch (err: unknown) {
      const errorMsg = parseErrorMessage(err);
      setError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch a single template by ID
   */
  const fetchTemplate = async (templateId: string): Promise<AdTemplate | null> => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase.from("ad_templates").select("*").eq("id", templateId).single();

      if (fetchError) throw fetchError;
      
      // Cast the data to AdTemplate type
      const template = data as unknown as AdTemplate;

      return template;
    } catch (err: unknown) {
      const errorMsg = parseErrorMessage(err);
      setError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create a new template
   */
  const createTemplate = async (templateData: AdTemplateFormData): Promise<AdTemplate | null> => {
    setLoading(true);
    setError(null);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error("User not authenticated");
      }

      const { data, error: createError } = await supabase
        .from("ad_templates")
        .insert({
          ...templateData,
          sitelinks: templateData.sitelinks as unknown,
          user_id: user.id,
        } as any)
        .select()
        .single();

      if (createError) throw createError;

      toast({
        title: "Success",
        description: "Template created successfully",
      });

      // Refresh templates list
      await fetchTemplates();

      return data;
    } catch (err: unknown) {
      const errorMsg = parseErrorMessage(err);
      setError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update an existing template
   */
  const updateTemplate = async (
    templateId: string,
    templateData: Partial<AdTemplateFormData>,
  ): Promise<AdTemplate | null> => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: updateError } = await supabase
        .from("ad_templates")
        .update(templateData as any)
        .eq("id", templateId)
        .select()
        .single();

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Template updated successfully",
      });

      // Refresh templates list
      await fetchTemplates();

      return data;
    } catch (err: unknown) {
      const errorMsg = parseErrorMessage(err);
      setError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete a template
   */
  const deleteTemplate = async (templateId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const { error: deleteError } = await supabase.from("ad_templates").delete().eq("id", templateId);

      if (deleteError) throw deleteError;

      toast({
        title: "Success",
        description: "Template deleted successfully",
      });

      // Refresh templates list
      await fetchTemplates();

      return true;
    } catch (err: unknown) {
      const errorMsg = parseErrorMessage(err);
      setError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Duplicate a template
   */
  const duplicateTemplate = async (templateId: string): Promise<AdTemplate | null> => {
    setLoading(true);
    setError(null);

    try {
      // Fetch the original template
      const original = await fetchTemplate(templateId);
      if (!original) {
        throw new Error("Template not found");
      }

      // Create a copy with modified name
      const { id, user_id, created_at, updated_at, ...templateData } = original;
      const copyData = {
        ...templateData,
        sitelinks: Array.isArray(templateData.sitelinks) ? templateData.sitelinks : [],
        name: `${original.name} (Copy)`,
      } as AdTemplateFormData;

      return await createTemplate(copyData);
    } catch (err: unknown) {
      const errorMsg = parseErrorMessage(err);
      setError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    templates,
    loading,
    error,
    fetchTemplates,
    fetchTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
  };
}
